import React, { useEffect, useId, useRef, useState } from 'react';
import { buildUpdateAdminProspectPropertyDataRequest } from '../../services/admin/prospectsListRequest';
import { updateAdminProspectPropertyData } from '../../services/admin/prospectsService';
import {
  adminProspectListErrorKey,
  classifyAdminCallableError,
} from './adminProspectDisplay';

const REGISTRY_ACTIVITY_TYPES = [
  'sale',
  'mortgage',
  'transmission',
  'assignment_or_donation',
  'undivided_sale',
  'sixty_day_notice',
  'correction_assignment',
  'other',
];

function formFromDetail(detail) {
  const profile = detail?.latestImportedPropertyProfile || {};
  const assessment = detail?.latestMunicipalAssessment || {};
  const activity = detail?.latestRegistryActivity || {};
  const lotNumbers = (detail?.lotNumbers || profile.lotNumbers || []).join(', ');
  return {
    lotNumbers,
    cadastralDivision: detail?.cadastralDivision || profile.cadastralDivision || '',
    municipalRollNumber: detail?.municipalRollNumber || profile.municipalRollNumber || '',
    numberOfFloors: profile.numberOfFloors == null ? '' : String(profile.numberOfFloors),
    constructionYear: profile.constructionYear == null ? '' : String(profile.constructionYear),
    landAreaValue: profile.landArea?.value == null ? '' : String(profile.landArea.value),
    landAreaUnit: profile.landArea?.unit || 'm2',
    assessmentTotal: assessment.totalCents == null ? '' : String(assessment.totalCents / 100),
    assessmentLand: assessment.landCents == null ? '' : String(assessment.landCents / 100),
    assessmentBuilding: assessment.buildingCents == null ? '' : String(assessment.buildingCents / 100),
    assessmentReferenceYear: assessment.referenceYear == null ? '' : String(assessment.referenceYear),
    assessmentReferenceDate: assessment.referenceDate || '',
    activityType: activity.activityType || '',
    activityOriginalLabel: activity.originalLabel || '',
    activityDate: activity.activityDate || '',
    considerationDollars:
      activity.considerationCents == null ? '' : String(activity.considerationCents / 100),
    registrationNumber: activity.registrationNumber || '',
    transferredPercentage:
      activity.transferredPercentage == null ? '' : String(activity.transferredPercentage),
    numberOfUnits: detail?.numberOfUnits == null ? '' : String(detail.numberOfUnits),
  };
}

function parseDollarsToCents(raw) {
  const text = String(raw || '').trim();
  if (!text) return undefined;
  const normalized = text.replace(/[\s\u00a0\u202f]/g, '').replace(',', '.');
  if (!/^-?\d+(\.\d{1,2})?$/.test(normalized)) return null;
  const dollars = Number(normalized);
  if (!Number.isFinite(dollars) || dollars < 0) return null;
  return Math.round(dollars * 100);
}

function parseOptionalInt(raw) {
  const text = String(raw || '').trim();
  if (!text) return undefined;
  const n = Number(text);
  if (!Number.isInteger(n)) return null;
  return n;
}

function buildPatch(form) {
  const patch = {};

  if (form.lotNumbers.trim()) {
    patch.lotNumbers = form.lotNumbers
      .split(/[;,|/]/)
      .map((part) => part.trim())
      .filter(Boolean);
  } else {
    patch.lotNumbers = [];
  }

  patch.cadastralDivision = form.cadastralDivision.trim() || null;
  patch.municipalRollNumber = form.municipalRollNumber.trim() || null;

  const floors = parseOptionalInt(form.numberOfFloors);
  if (floors === null) return { error: 'numberOfFloors' };
  if (floors !== undefined) patch.numberOfFloors = floors === 0 ? null : floors;

  const year = parseOptionalInt(form.constructionYear);
  if (year === null) return { error: 'constructionYear' };
  if (year !== undefined) patch.constructionYear = year === 0 ? null : year;

  const landValue = form.landAreaValue.trim();
  if (landValue) {
    const value = Number(landValue.replace(',', '.'));
    if (!Number.isFinite(value) || value <= 0) return { error: 'landArea' };
    patch.landArea = { value, unit: form.landAreaUnit === 'ft2' ? 'ft2' : 'm2' };
  } else {
    patch.landArea = null;
  }

  const municipalAssessment = {};
  const totalCents = parseDollarsToCents(form.assessmentTotal);
  if (totalCents === null) return { error: 'assessmentTotal' };
  if (totalCents !== undefined) municipalAssessment.totalCents = totalCents;
  const landCents = parseDollarsToCents(form.assessmentLand);
  if (landCents === null) return { error: 'assessmentLand' };
  if (landCents !== undefined) municipalAssessment.landCents = landCents;
  const buildingCents = parseDollarsToCents(form.assessmentBuilding);
  if (buildingCents === null) return { error: 'assessmentBuilding' };
  if (buildingCents !== undefined) municipalAssessment.buildingCents = buildingCents;
  const referenceYear = parseOptionalInt(form.assessmentReferenceYear);
  if (referenceYear === null) return { error: 'assessmentReferenceYear' };
  if (referenceYear !== undefined) municipalAssessment.referenceYear = referenceYear;
  if (form.assessmentReferenceDate.trim()) {
    municipalAssessment.referenceDate = form.assessmentReferenceDate.trim();
  } else {
    municipalAssessment.referenceDate = null;
  }
  patch.municipalAssessment = municipalAssessment;

  const registryActivity = {};
  if (form.activityType) registryActivity.activityType = form.activityType;
  else registryActivity.activityType = null;
  registryActivity.originalLabel = form.activityOriginalLabel.trim() || null;
  registryActivity.activityDate = form.activityDate.trim() || null;
  const considerationCents = parseDollarsToCents(form.considerationDollars);
  if (considerationCents === null) return { error: 'consideration' };
  if (considerationCents !== undefined) registryActivity.considerationCents = considerationCents;
  registryActivity.registrationNumber = form.registrationNumber.trim() || null;
  const transferred = form.transferredPercentage.trim();
  if (transferred) {
    const pct = Number(transferred.replace(',', '.'));
    if (!Number.isFinite(pct) || pct < 0 || pct > 100) return { error: 'transferredPercentage' };
    registryActivity.transferredPercentage = pct;
  } else {
    registryActivity.transferredPercentage = null;
  }
  patch.registryActivity = registryActivity;

  const units = parseOptionalInt(form.numberOfUnits);
  if (units === null) return { error: 'numberOfUnits' };
  if (units !== undefined) patch.numberOfUnits = units > 0 ? units : null;

  return { patch };
}

export default function AdminEditProspectPropertyDataDialog({ open, prospect, t, onClose, onSaved }) {
  const titleId = useId();
  const errorId = useId();
  const firstRef = useRef(null);
  const [form, setForm] = useState(() => formFromDetail(prospect));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !prospect) return;
    setForm(formFromDetail(prospect));
    setSaving(false);
    setError('');
    const id = window.setTimeout(() => firstRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, [open, prospect]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !saving) {
        event.preventDefault();
        onClose?.();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, saving, onClose]);

  if (!open || !prospect) return null;

  const setField = (name) => (event) => {
    const value = event.target.value;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (saving) return;
    const built = buildPatch(form);
    if (built.error) {
      setError(t('admin.prospection.error.invalid'));
      return;
    }
    setSaving(true);
    setError('');
    try {
      await updateAdminProspectPropertyData(
        buildUpdateAdminProspectPropertyDataRequest(
          prospect.id,
          built.patch,
          prospect.updatedAtVersion || prospect.updatedAt,
        ),
      );
      onSaved?.();
      onClose?.();
    } catch (err) {
      setError(t(adminProspectListErrorKey(classifyAdminCallableError(err))));
      setSaving(false);
    }
  };

  const fieldClass =
    'mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60';

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/50 p-4 sm:items-center">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-xl bg-white p-4 shadow-xl"
      >
        <h2 id={titleId} className="text-lg font-semibold text-slate-900">
          {t('admin.prospection.importedProperty.editTitle')}
        </h2>
        <p className="mt-1 text-sm text-slate-600">{t('admin.prospection.importedProperty.editHint')}</p>

        <form onSubmit={handleSubmit} className="mt-4 space-y-4">
          <fieldset className="space-y-3 rounded-lg border border-slate-200 p-3">
            <legend className="px-1 text-sm font-semibold text-slate-800">
              {t('admin.prospection.importedProperty.section.cadastral')}
            </legend>
            <label className="block text-sm">
              <span className="font-medium text-slate-700">{t('admin.prospection.importedProperty.lotNumbers')}</span>
              <input
                ref={firstRef}
                type="text"
                value={form.lotNumbers}
                onChange={setField('lotNumbers')}
                className={fieldClass}
              />
            </label>
            <label className="block text-sm">
              <span className="font-medium text-slate-700">
                {t('admin.prospection.importedProperty.cadastralDivision')}
              </span>
              <input type="text" value={form.cadastralDivision} onChange={setField('cadastralDivision')} className={fieldClass} />
            </label>
            <label className="block text-sm">
              <span className="font-medium text-slate-700">
                {t('admin.prospection.importedProperty.municipalRollNumber')}
              </span>
              <input
                type="text"
                value={form.municipalRollNumber}
                onChange={setField('municipalRollNumber')}
                className={fieldClass}
              />
            </label>
          </fieldset>

          <fieldset className="space-y-3 rounded-lg border border-slate-200 p-3">
            <legend className="px-1 text-sm font-semibold text-slate-800">
              {t('admin.prospection.importedProperty.section.characteristics')}
            </legend>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block text-sm">
                <span className="font-medium text-slate-700">
                  {t('admin.prospection.importedProperty.numberOfFloors')}
                </span>
                <input type="number" min="0" value={form.numberOfFloors} onChange={setField('numberOfFloors')} className={fieldClass} />
              </label>
              <label className="block text-sm">
                <span className="font-medium text-slate-700">
                  {t('admin.prospection.importedProperty.constructionYear')}
                </span>
                <input type="number" min="0" value={form.constructionYear} onChange={setField('constructionYear')} className={fieldClass} />
              </label>
              <label className="block text-sm">
                <span className="font-medium text-slate-700">
                  {t('admin.prospection.importedProperty.numberOfUnits')}
                </span>
                <input type="number" min="0" value={form.numberOfUnits} onChange={setField('numberOfUnits')} className={fieldClass} />
              </label>
              <label className="block text-sm">
                <span className="font-medium text-slate-700">
                  {t('admin.prospection.importedProperty.landArea')}
                </span>
                <div className="mt-1 flex gap-2">
                  <input type="text" value={form.landAreaValue} onChange={setField('landAreaValue')} className={fieldClass} />
                  <select value={form.landAreaUnit} onChange={setField('landAreaUnit')} className="rounded-lg border border-slate-300 px-2 text-sm">
                    <option value="m2">{t('admin.prospection.importedProperty.landArea.unit.m2')}</option>
                    <option value="ft2">{t('admin.prospection.importedProperty.landArea.unit.ft2')}</option>
                  </select>
                </div>
              </label>
            </div>
          </fieldset>

          <fieldset className="space-y-3 rounded-lg border border-slate-200 p-3">
            <legend className="px-1 text-sm font-semibold text-slate-800">
              {t('admin.prospection.importedProperty.section.assessment')}
            </legend>
            <div className="grid gap-3 sm:grid-cols-2">
              {[
                ['assessmentTotal', 'admin.prospection.importedProperty.assessment.total'],
                ['assessmentLand', 'admin.prospection.importedProperty.assessment.land'],
                ['assessmentBuilding', 'admin.prospection.importedProperty.assessment.building'],
              ].map(([name, labelKey]) => (
                <label key={name} className="block text-sm">
                  <span className="font-medium text-slate-700">{t(labelKey)}</span>
                  <input type="text" inputMode="decimal" value={form[name]} onChange={setField(name)} className={fieldClass} />
                </label>
              ))}
              <label className="block text-sm">
                <span className="font-medium text-slate-700">
                  {t('admin.prospection.importedProperty.assessment.referenceYear')}
                </span>
                <input
                  type="number"
                  value={form.assessmentReferenceYear}
                  onChange={setField('assessmentReferenceYear')}
                  className={fieldClass}
                />
              </label>
              <label className="block text-sm">
                <span className="font-medium text-slate-700">
                  {t('admin.prospection.importedProperty.assessment.referenceDate')}
                </span>
                <input
                  type="date"
                  value={form.assessmentReferenceDate}
                  onChange={setField('assessmentReferenceDate')}
                  className={fieldClass}
                />
              </label>
            </div>
          </fieldset>

          <fieldset className="space-y-3 rounded-lg border border-slate-200 p-3">
            <legend className="px-1 text-sm font-semibold text-slate-800">
              {t('admin.prospection.importedProperty.section.registry')}
            </legend>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block text-sm">
                <span className="font-medium text-slate-700">
                  {t('admin.prospection.importedProperty.registry.activityType')}
                </span>
                <select value={form.activityType} onChange={setField('activityType')} className={fieldClass}>
                  <option value="">{t('admin.prospection.importedProperty.unavailable')}</option>
                  {REGISTRY_ACTIVITY_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {t(`admin.prospection.importedProperty.registryActivity.type.${type}`)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-sm">
                <span className="font-medium text-slate-700">
                  {t('admin.prospection.importedProperty.registry.originalLabel')}
                </span>
                <input type="text" value={form.activityOriginalLabel} onChange={setField('activityOriginalLabel')} className={fieldClass} />
              </label>
              <label className="block text-sm">
                <span className="font-medium text-slate-700">
                  {t('admin.prospection.importedProperty.registry.activityDate')}
                </span>
                <input type="date" value={form.activityDate} onChange={setField('activityDate')} className={fieldClass} />
              </label>
              <label className="block text-sm">
                <span className="font-medium text-slate-700">
                  {t('admin.prospection.importedProperty.registry.consideration')}
                </span>
                <input type="text" inputMode="decimal" value={form.considerationDollars} onChange={setField('considerationDollars')} className={fieldClass} />
              </label>
              <label className="block text-sm">
                <span className="font-medium text-slate-700">
                  {t('admin.prospection.importedProperty.registry.registrationNumber')}
                </span>
                <input type="text" value={form.registrationNumber} onChange={setField('registrationNumber')} className={fieldClass} />
              </label>
              <label className="block text-sm">
                <span className="font-medium text-slate-700">
                  {t('admin.prospection.importedProperty.registry.transferredPercentage')}
                </span>
                <input type="text" inputMode="decimal" value={form.transferredPercentage} onChange={setField('transferredPercentage')} className={fieldClass} />
              </label>
            </div>
          </fieldset>

          {error ? (
            <p id={errorId} className="text-sm text-red-700" role="alert">
              {error}
            </p>
          ) : null}

          <div className="flex flex-wrap justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
            >
              {t('admin.prospection.edit.cancel')}
            </button>
            <button
              type="submit"
              disabled={saving}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
            >
              {saving ? t('admin.prospection.edit.saving') : t('admin.prospection.edit.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
